import groupBy from 'lodash/groupBy';
import type { IBetCount } from '@/interface/IBetCount';
import type { IBet, IMyBetsResponse, IPartialCashOutBet, IRejectReason, ISubBet } from '@/interface/IMyBetsResponse';
import { IScore } from '@/interface/IScore';
import type { IBetBuilderLeg } from '@/interface/betBuilder';
import { BetStatus, MainBetStatus, MarketType, OddsOption, PlaceOrderStatus, PriceStyle, SportType, SubBetStatus, TimezoneOffset } from '@/interface/enum';
import type { Bet, BetBuilderLeg, FailBetReason, MyBetsResponse, PartiallyCashedOutBet, RunningBetCountResponse, SubBet } from '@/interface/pollux';
import { getTimeWithGMT, parseISOString } from '@/libraries/utils/date';

export function formatRunningBetCount(betCount: RunningBetCountResponse): IBetCount {
    return {
        singleBet: betCount.singleBet,
        mixParlayBet: betCount.multipleBet,
        outrightBet: betCount.outrightBet,
        cashOutBet: betCount.cashOutBet,
        cashOutMixParlayBet: betCount.cashOutMixParlayBet,
        betBuilderBetCount: betCount.betBuilderBetCount,
    };
}

function formatFailBetReason(failBetReason: FailBetReason): IRejectReason {
    return {
        status: PlaceOrderStatus.fromPollux(failBetReason.status),
        extraInfo: failBetReason.extraInfo,
    };
}

export function formatMyBets(myBets: MyBetsResponse): IMyBetsResponse {
    const runningBets = myBets.runningBets ? myBets.runningBets.map(bet => formatBet(bet)) : null;
    const settledBets = myBets.settledBets ? myBets.settledBets.map(bet => formatBet(bet)) : null;

    return {
        running: runningBets,
        settled: settledBets,
    };
}

export function formatBet(bet: Bet): IBet {
    const marketType = MarketType.fromPollux(bet.marketType);
    const mainBetStatus = MainBetStatus.fromPollux(bet.mainBetStatus);

    return {
        resultDisplayDate: marketType === MarketType.OutRight ? bet.winLostDate : bet.eventDate,
        halfTimeScore: IScore.fromPollux(bet.halfTimeScore),
        fullTimeScore: IScore.fromPollux(bet.fullTimeScore),
        winLost: bet.playerWinLoss,
        commission: bet.playerCommission,
        stake: bet.stake,
        actualStake: bet.actualStake,
        oddsOptionType: OddsOption.fromPollux(bet.oddsOptionType),
        isLive: bet.isLive,
        betLiveScore: IScore.fromPollux(bet.betLiveScore),
        betCondition: bet.betCondition,
        homeTeamName: bet.homeTeamName,
        awayTeamName: bet.awayTeamName,
        leagueName: bet.leagueName ?? '',
        sportType: SportType.fromPollux(bet.sportType),
        sportName: bet.sportName ?? '',
        betTeam: bet.betTeam,
        betTypeGroupName: bet.betTypeGroup,
        point: bet.point,
        marketTypeString: bet.marketTypeName ?? '',
        marketType: MarketType.fromPollux(bet.marketType),
        oddsPrice: bet.odds,
        oddsId: bet.oddsId,
        status: mainBetStatus,
        transId: bet.transactionId,
        selectionId: bet.selectionId,
        id: bet.id,
        transDate: getTimeWithGMT(parseISOString(bet.transactionDate), TimezoneOffset.Server),
        oddsStyle: PriceStyle.fromPollux(bet.oddsStyle) ?? PriceStyle.Unknown,
        betTypeGroupId: bet.betTypeGroupId,
        matchId: bet.matchId,
        subBetDetail: bet.subBets ? bet.subBets.map(subBet => formatSubBet(subBet)) : [],
        isSupportCO: false,
        isFreeBet: bet.isVoucher,
        isSpecialVoid: mainBetStatus === MainBetStatus.SpecialVoid,
        isResettled: [MainBetStatus.Amended, MainBetStatus.Reversed].includes(mainBetStatus),
        isSettled: ![MainBetStatus.Running, MainBetStatus.Waiting].includes(mainBetStatus),
        failBetReason: bet.failBetReason ? formatFailBetReason(bet.failBetReason) : undefined,
        partiallyCashedOutBets: bet.partiallyCashedOutBets ? formatPartialCashOutMap(bet.partiallyCashedOutBets) : undefined,
        originalTransId: bet.originalTransactionId,
        originalStake: bet.originalStake,
        originalActualStake: bet.originalActualStake,
        totalCashedOutValue: bet.totalCashedOutValue,
        isPartialCashOutBet: bet.isPartialCashOutBet,
        partialCashedOutStake: bet.partialCashedOutStake,
        partialCashedOutValue: bet.partialCashedOutValue,
        partialCashedOutActualStake: bet.partialCashedOutActualStake,
        betBuilderLegs: bet.betBuilderLegs ? bet.betBuilderLegs.map(betBuilderLeg => formatBetBuilderLeg(betBuilderLeg)) : [],
        hasPartiallyCashedOutBets: (bet.partiallyCashedOutBets?.length ?? 0) > 0,
        parlayFinalOdds: bet.parlayFinalOdds,
    };
}

function formatSubBet(subBet: SubBet): ISubBet {
    return {
        resultDisplayDate: subBet.eventDate,
        oddsOptionType: OddsOption.fromPollux(subBet.oddsOptionType),
        betLiveScore: IScore.fromPollux(subBet.betLiveScore),
        sportType: SportType.fromPollux(subBet.sportType),
        betTeam: subBet.betTeam ?? '',
        marketType: MarketType.fromPollux(subBet.marketType),
        displayBetTeam: subBet.betTeam ?? '',
        matchId: subBet.matchId,
        halfTimeScore: IScore.fromPollux(subBet.halfTimeScore),
        fullTimeScore: IScore.fromPollux(subBet.fullTimeScore),

        isLive: subBet.isLive,
        point: subBet.point,
        leagueName: subBet.leagueName ?? '',
        betTypeGroupName: subBet.betTypeGroup ?? '',
        awayTeamName: subBet.awayTeamName ?? '',
        homeTeamName: subBet.homeTeamName ?? '',
        oddsPrice: subBet.odds ?? '',
        marketTypeString: subBet.marketTypeName ?? '',
        sportName: subBet.sportName ?? '',
        betCondition: subBet.betCondition,
        status: SubBetStatus.fromPollux(subBet.subBetStatus),
        leagueId: subBet.leagueId,
        betTypeGroupId: subBet.betTypeGroupId,
    };
}

function formatBetBuilderLeg(leg: BetBuilderLeg): IBetBuilderLeg {
    return {
        transactionId: leg.transactionId,
        isLive: leg.isLive,
        status: SubBetStatus.fromPollux(leg.status),
        refNo: leg.refNo,
        marketType: {
            id: leg.marketType?.key ?? 0,
            name: leg.marketType?.value ?? '',
        },
        selectionType: {
            id: leg.selectionType?.key ?? 0,
            name: leg.selectionType?.value ?? '',
        },
        betTeam: leg.betTeam ?? '',
        point: leg.point,
        betLiveScore: IScore.fromPollux(leg.betLiveScore),
    };
}

function formatPartialCashOutBet(bet: PartiallyCashedOutBet): IPartialCashOutBet {
    return {
        transId: bet.transId,
        status: bet.partialCashOutBetStatus,
        transDate: getTimeWithGMT(parseISOString(bet.transDate), TimezoneOffset.Server),
        stake: bet.stake,
        actualStake: bet.actualStake,
        result: bet.result,
        isResettled: (bet.partialCashOutBetStatus & BetStatus.Resettled) === BetStatus.Resettled,
        isVoid: [BetStatus.Void, BetStatus.SpecialVoid, BetStatus.SystemVoid].some(status => (bet.partialCashOutBetStatus & status) === status),
        isCashedOut: (bet.partialCashOutBetStatus & BetStatus.CashedOut) === BetStatus.CashedOut,
    };
}

function formatPartialCashOutMap(bets: PartiallyCashedOutBet[]): Map<number, IPartialCashOutBet[]> {
    const groupedBets = groupBy(bets, x => x.transId);
    return new Map(
        Object.entries(groupedBets).map(
            ([transId, bets]) => [parseInt(transId), bets.map(formatPartialCashOutBet)],
        ),
    );
}

export function convertPriceStyle(priceStyle: string): PriceStyle {
    const priceStyleMap: Record<string, PriceStyle> = {
        M: PriceStyle.Malay,
        H: PriceStyle.HK,
        I: PriceStyle.Indo,
        E: PriceStyle.Euro,
    };

    return priceStyleMap[priceStyle] ?? PriceStyle.Unknown;
}

export function isIBet(bet: IBet | ISubBet): bet is IBet {
    return 'transDate' in bet;
}
